/**
 * 실물 상세 — /inventory/units/:id (VOCABULARY "실물 상세" · 현민 2026-10-15). 재고 현황의 번호를 눌러 새 탭으로 여는 한 장.
 * 제품 상세와 같은 1:1 배치 — 왼쪽 기본 정보 · 번호 · 구성(자식 실물), 오른쪽 제품 링크 · 메모 · 이력(inventory_event).
 * 머리에 재고 조정 버튼 둘 — [사용 불가](사유 창) · [다시 찾음](2026-11-03). 검수 · 이동 · 홀딩은 재고 현황의 [작업 ▾].
 */
import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
import { costTextOf } from '../lib/money';
import type { UnitDetail } from '../api/client';
import { PRODUCT_SHAPE_WORD, productShapeOf } from '../api/client';
import { ROW_NO_HEAD, rowNoCell } from '../components/row-no';
import { ORIGIN_WORD, REASON_WORD, eventWordOf, renderUnitTable, unitStatusWordOf, valueWordOf } from '../components/unit-table';
import { escapeHtml, fmtDate, fmtDateTime } from '../lib/cells';
import { assert } from '../lib/assert';
import { RETIRED_REASON_WORD, isRetireReady, renderRetireDialog, retireBlockOf, retireInputOf } from '../components/retire-dialog';
import type { RetireDraft } from '../components/retire-dialog';

interface UnitDetailData {
  id: string;
  detail: UnitDetail | null;
  loading: boolean;
  error: string;
  retire: RetireDraft | null;   // [사용 불가] 창. null = 닫힘
  acting: boolean;              // 사용 불가 · 다시 찾음 보내는 중
  actionError: string;          // 그 둘에서 난 오류 글. '' = 없음
}

const NOT_FOUND_PREFIX = 'HTTP 404';
/** 이력 type → 말. 트리거가 바뀐 열로 정한 것(328) */

export class UnitDetailView extends SolaView<UnitDetailData> {
  constructor(params: { id: string }) {
    super();
    this.data = { id: params.id, detail: null, loading: true, error: '', retire: null, acting: false, actionError: '' };
  }

  override events = {
    click:  { '@retire-open': 'handleRetireOpen', '@retire-cancel': 'handleRetireCancel', '@retire-confirm': 'handleRetireConfirm', '@restore': 'handleRestore' },
    change: { '@retire-reason': 'handleRetireReason' },
    input:  { '@retire-note': 'handleRetireNote' },
  };

  override getTitle(): string {
    return `실물 #${this.data.id} — HCLTech`;
  }

  override onMount(): void {
    void this.load();
  }

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <div class="page page-unit-detail pv-page">
        <header class="page-header">
          <h1>📦 실물 상세</h1>
          <a class="btn-ghost" data-link href="/preview/inventory">재고 현황으로</a>
        </header>
        <section class="page-body pv-body">${this.renderBody()}</section>
      </div>`;
  }

  private async load(): Promise<void> {
    this.data = { ...this.data, loading: true, error: '' };
    this.refreshRegion('.pv-body', () => this.renderBody());
    try {
      const detail = await api.fetchUnitDetail(this.data.id);
      this.data = { ...this.data, detail, loading: false };
    } catch (e) {
      this.data = { ...this.data, loading: false, error: e instanceof Error ? e.message : String(e) };
    }
    this.refreshRegion('.pv-body', () => this.renderBody());
  }

  private renderBody(): string {
    const { detail, loading, error } = this.data;
    if (loading) return '<p class="text-muted">불러오는 중…</p>';
    if (error.startsWith(NOT_FOUND_PREFIX)) return `<p class="text-muted">없는 실물입니다 — #${escapeHtml(this.data.id)}</p>`;
    if (error !== '') return `<p class="text-muted">불러오지 못했습니다 — ${escapeHtml(error)}</p>`;
    if (detail === null) return '';
    return `
      ${renderHead(detail, this.data.acting)}
      ${this.data.actionError === '' ? '' : `<p class="pv-action-error">${escapeHtml(this.data.actionError)}</p>`}
      ${this.data.retire === null ? '' : renderRetireDialog(this.data.retire, this.data.acting)}
      <div class="pd-layout">
        <div class="pd-main">
          ${renderBasics(detail)}
          ${renderNumbers(detail)}
          ${renderChildren(detail)}
        </div>
        <aside class="pd-side">
          ${renderProduct(detail)}
          ${renderNotes(detail)}
          ${renderEvents(detail)}
        </aside>
      </div>`;
  }

  // ─── 재고 조정 — 사용 불가 · 다시 찾음(2026-11-03) ───
  handleRetireOpen(): void {
    this.data = { ...this.data, retire: { unit_ids: [this.data.id], reason: 'lost', note: '' }, actionError: '' };
    this.refreshRegion('.pv-body', () => this.renderBody());
  }

  handleRetireCancel(): void {
    if (this.data.acting) return;
    this.data = { ...this.data, retire: null };
    this.refreshRegion('.pv-body', () => this.renderBody());
  }

  handleRetireReason(_e: Event, el: HTMLElement): void {
    const reason = api.E_RETIRED_REASONS.find((value) => value === (el as HTMLInputElement).value);
    assert(reason !== undefined && this.data.retire !== null, '사유 라디오는 [사용 불가] 창 안에만 있고 값은 E_RETIRED_REASONS 중 하나 — renderRetireDialog 가 그렸다');
    this.data = { ...this.data, retire: { ...this.data.retire, reason } };
    this.refreshRegion('.pv-body', () => this.renderBody());
  }

  /** 글자마다 — 다시 그리면 포커스가 날아가니 [사용 불가로] 버튼의 잠김만 손으로 */
  handleRetireNote(_e: Event, el: HTMLElement): void {
    const draft = this.data.retire;
    assert(draft !== null, '글 칸은 [사용 불가] 창 안에만 있다');
    const next = { ...draft, note: (el as HTMLInputElement).value };
    this.data = { ...this.data, retire: next };
    const button = this._rootElement?.querySelector<HTMLButtonElement>('button[data-action="retire-confirm"]');
    if (button) button.disabled = !isRetireReady(next) || this.data.acting;
  }

  async handleRetireConfirm(): Promise<void> {
    const draft = this.data.retire;
    assert(draft !== null && isRetireReady(draft), '[사용 불가로] 는 창이 열려 있고 기타면 글이 있을 때만 켜진다 — renderRetireDialog 가 disabled 를 붙였다');
    await this.act(async () => { await api.retireUnits(retireInputOf(draft)); return api.fetchUnitDetail(this.data.id); }, '사용 불가');
  }

  async handleRestore(): Promise<void> {
    if (!window.confirm('다시 찾음 — 사용 불가에서 검수 중으로 돌립니다.')) return;
    await this.act(() => api.restoreUnit(this.data.id), '다시 찾음');
  }

  /** 보내고 바뀐 상세로 다시 그린다. 실패면 오류 글 — 상세는 그대로 */
  private async act(send: () => Promise<UnitDetail>, word: string): Promise<void> {
    this.data = { ...this.data, acting: true, actionError: '' };
    this.refreshRegion('.pv-body', () => this.renderBody());
    try {
      const detail = await send();
      this.data = { ...this.data, detail, acting: false, retire: null };
    } catch (e) {
      this.data = { ...this.data, acting: false, retire: null, actionError: `${word} 안 됨 — ${e instanceof Error ? e.message : String(e)}` };
    }
    this.refreshRegion('.pv-body', () => this.renderBody());
  }
}

// ─── 카드들(순수) ───
/** 머리의 재고 조정 버튼 — 옮길 수 있으면 [사용 불가], 사용 불가(서버 안 부품 아님)면 [다시 찾음]. 막히면 회색 + 까닭 */
function renderAdjustButtons(detail: UnitDetail, bActing: boolean): string {
  if (detail.stock_status === 'retired') {
    const bChild = detail.parent_unit_id !== null;
    return `<button type="button" class="btn-ghost" data-action="restore" ${bChild || bActing ? 'disabled' : ''} ${bChild ? 'title="서버 안 부품 — 서버째 되돌립니다"' : ''}>다시 찾음</button>`;
  }
  const blockOrNull = retireBlockOf(detail);
  return `<button type="button" class="btn-ghost" data-action="retire-open" ${blockOrNull !== null || bActing ? 'disabled' : ''} ${blockOrNull === null ? '' : `title="${escapeHtml(blockOrNull)}"`}>사용 불가</button>`;
}

/** 머리 — 번호 · 파트넘버 · 설명, 칩: 상태 · 컨디션 · 타입 · 안에 n장 */
function renderHead(detail: UnitDetail, bActing: boolean): string {
  return `
    <div class="form-card pd-head">
      <div class="pd-head-row">
        <div class="pd-title">
          <span class="pd-pn mono">${detail.option_part === null ? '—' : escapeHtml(detail.option_part)}</span>
          <span class="pd-desc">${detail.description === null ? '<span class="text-muted">설명 없음</span>' : escapeHtml(detail.description)}</span>
        </div>
        <div class="pd-head-right">${renderAdjustButtons(detail, bActing)}<span class="pl-id mono">#${escapeHtml(detail.id)}</span></div>
      </div>
      <div class="pd-chips">
        <span class="chip pv-st-${detail.stock_status}">${unitStatusWordOf(detail.stock_status, detail.sales_status)} <small class="mono">${detail.stock_status}</small></span>
        <span class="chip chip-muted">${escapeHtml(detail.condition)}</span>
        <span class="chip chip-muted">${PRODUCT_SHAPE_WORD[productShapeOf(detail.child_count)]}</span>
        ${detail.child_count > 0 ? `<span class="chip chip-primary">안에 ${detail.child_count}장</span>` : ''}
        ${detail.is_held ? `<span class="chip pv-hold" title="${escapeHtml(detail.hold_note === null ? '' : detail.hold_note)}">홀딩</span>` : ''}
        ${detail.parent === null ? '' : `<span class="chip chip-primary">#${escapeHtml(detail.parent.id)} 안에</span>`}
      </div>
    </div>`;
}

/** 사용 불가 사유의 말 — 서버가 준 값이 넷 밖이면 날것 그대로 */
function retiredReasonText(reasonOrNull: string | null): string {
  if (reasonOrNull === null) return '—';
  const reason = api.E_RETIRED_REASONS.find((value) => value === reasonOrNull);
  return reason === undefined ? escapeHtml(reasonOrNull) : RETIRED_REASON_WORD[reason];
}

function kv(key: string, value: string): string {
  return `<div class="kv-row"><span class="kv-key">${key}</span><span class="kv-val">${value}</span></div>`;
}
const dash = (text: string | null): string => (text === null || text.trim() === '' ? '—' : escapeHtml(text));
const mono = (text: string | null): string => (text === null || text.trim() === '' ? '—' : `<span class="mono">${escapeHtml(text)}</span>`);

/** 기본 정보 — 실물의 축(상태 · 컨디션 · 창고 · 출처) + 원가 · 품목코드 · 묶인 곳 · 생긴 날 */
function renderBasics(detail: UnitDetail): string {
  const cost = detail.cost_amount === null ? '—' : `<span class="mono">${escapeHtml(costTextOf(detail.cost_amount, detail.cost_currency))}</span>`;
  const bound = detail.quote_no === null ? '—' : `견적 <span class="mono">${escapeHtml(detail.quote_no)}</span>`;
  const retired = detail.stock_status === 'retired' ? kv('사용 불가 사유', retiredReasonText(detail.retired_reason)) : '';
  return `
    <section class="form-card pd-card">
      <h2>기본 정보 <small>이 한 장의 축 — 상태 · 컨디션 · 창고는 실물의 것, PN · 카테고리는 제품의 것</small></h2>
      <div class="pd-kv-grid">
        ${kv('상태', `${unitStatusWordOf(detail.stock_status, detail.sales_status)} <small class="mono text-muted">${detail.stock_status}</small>`)}
        ${detail.is_held ? kv('홀딩', escapeHtml(detail.hold_note === null ? '' : detail.hold_note)) : ''}
        ${detail.asset_note === null ? '' : kv('자산 메모', escapeHtml(detail.asset_note))}
        ${kv('컨디션', dash(detail.condition))}
        ${kv('창고', dash(detail.warehouse))}
        ${kv('출처', `${ORIGIN_WORD[detail.origin]} <small class="mono text-muted">${detail.origin}</small>`)}
        ${kv('원가', cost)}
        ${kv('이카운트 품목코드', mono(detail.ecount_item_code))}
        ${kv('묶인 곳', bound)}
        ${retired}
        ${kv('생긴 날', `<span class="mono">${escapeHtml(fmtDate(detail.created_at))}</span>`)}
      </div>
    </section>`;
}

/** 번호 — 제조사와 무관한 번호 다섯(328 ①-1). 제조사 고유 번호(SID · Service Tag)는 1:1 표 — 다음 */
function renderNumbers(detail: UnitDetail): string {
  return `
    <section class="form-card pd-card">
      <h2>번호 <small>이 한 장에만 붙는 번호. 비면 아직 안 읽은 것</small></h2>
      <div class="pd-kv-grid">
        ${kv('시리얼', mono(detail.serial_number))}
        ${kv('WWN', mono(detail.wwn))}
        ${kv('PSID', mono(detail.psid))}
        ${kv('MAC', mono(detail.mac))}
        ${kv('제조일', detail.manufacture_date === null ? '—' : `<span class="mono">${escapeHtml(fmtDate(detail.manufacture_date))}</span>`)}
        ${kv('제조자', dash(detail.manufacturer))}
      </div>
    </section>`;
}

/** 구성 — 이 장 안의 자식 실물(서버 · 세트). 부품이면 꽂힌 부모 한 줄 */
function renderChildren(detail: UnitDetail): string {
  if (detail.parent !== null) {
    return `
    <section class="form-card pd-card">
      <h2>꽂힌 곳 <small>이 장이 들어 있는 서버 · 세트</small></h2>
      ${renderUnitTable([detail.parent], { kind: 'read' }, '', 0)}
    </section>`;
  }
  if (detail.product_type === 'part') return '';
  return `
    <section class="form-card pd-card">
      <h2>구성 <small>이 장 안의 실물 ${detail.children.length}장 — 상태 · 창고는 이 장을 따라간다</small></h2>
      ${renderUnitTable(detail.children, { kind: 'read' }, '안에 든 실물이 없습니다', 0)}
    </section>`;
}

/** 제품 — 이 실물이 무엇인가는 product 가 말한다. 카탈로그는 거기서 */
function renderProduct(detail: UnitDetail): string {
  return `
    <section class="form-card pd-card">
      <h2>제품 <small>이 실물의 파는 단위 — 카탈로그 · 구성은 제품 상세에서</small></h2>
      <div class="pd-kv-grid">
        ${kv('제품번호', `<a class="mono" data-link href="/products/${escapeHtml(detail.hclt_product_id)}">${escapeHtml(detail.hclt_number)}</a>`)}
        ${kv('파트넘버', mono(detail.option_part))}
        ${kv('카테고리', dash(detail.spec_category))}
        ${kv('타입', PRODUCT_SHAPE_WORD[productShapeOf(detail.child_count)])}
      </div>
      <p class="pd-link"><a data-link href="/preview/inventory?product_id=${escapeHtml(detail.hclt_product_id)}">같은 제품의 실물 전부 →</a></p>
    </section>`;
}

function renderNotes(detail: UnitDetail): string {
  return `
    <section class="form-card pd-card">
      <h2>메모 <small>그 한 장의 일(B 뱅크에서만 인식 같은). PN 의 사실은 카탈로그 메모</small></h2>
      ${detail.notes === null || detail.notes.trim() === '' ? '<p class="text-muted">메모 없음</p>' : `<p class="pd-notes">${escapeHtml(detail.notes)}</p>`}
    </section>`;
}

/** 이력 — inventory_event, 최근 것부터. 입고 · 전이 · 값 바뀜(열 · 전 → 후 · 이유) */
function renderEvents(detail: UnitDetail): string {
  const rows = detail.events.map((event, index) => `
      <tr>
        ${rowNoCell(0, index)}
        <td class="pl-sub mono">${escapeHtml(fmtDateTime(event.created_at))}</td>
        <td>${eventWordOf(event.type, event.new_value)}${event.type === 'changed' && event.column_name !== null ? ` <span class="mono text-muted">${escapeHtml(event.column_name)}</span>` : ''}</td>
        <td class="pl-sub">${event.column_name === null ? '' : `${escapeHtml(valueWordOf(event.column_name, event.old_value))} → ${escapeHtml(valueWordOf(event.column_name, event.new_value))}`}</td>
        <td class="pl-sub">${event.reason === null ? '' : escapeHtml(REASON_WORD[event.reason] ?? event.reason)}${event.reason_note === null ? '' : ` · ${escapeHtml(event.reason_note)}`}</td>
        <td class="pl-sub">${dash(event.changed_by)}</td>
      </tr>`).join('');
  return `
    <section class="form-card pd-card">
      <h2>이력 <small>트리거가 남긴 열 단위 변경 ${detail.events.length}건</small></h2>
      <table class="pv-table pv-table-sm">
        <thead><tr>${ROW_NO_HEAD}<th>언제</th><th>무엇</th><th>전 → 후</th><th>이유</th><th>누가</th></tr></thead>
        <tbody>${rows === '' ? '<tr><td colspan="6" class="text-muted">이력이 없습니다</td></tr>' : rows}</tbody>
      </table>
    </section>`;
}
